// src/services/assetService.js
import { repo } from "../dbservice/repo";
import { AssetModel } from "../models/AssetModel";
import { stripUndefined } from "../utils/assetUtils";

class AssetService {
  constructor() {
    this.collectionName = "assets";
  }

  get currentUser() {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const data = localStorage.getItem("userData");
    try {
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("Failed to parse userData from localStorage", e);
      return null;
    }
  }

  async fetchAssets({
    companyId = "",
    locationCode = "",
    status = "",
    assetType = "",
    ownershipType = "",
    assignedTo = "",
  } = {}) {
    const user = this.currentUser;

    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId || "";
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }

    if (
      locationCode &&
      String(locationCode).trim() !== "" &&
      String(locationCode).toUpperCase() !== "ALL"
    ) {
      filters.push({ field: "locationCode", value: String(locationCode) });
    }

    if (status)        filters.push({ field: "status", value: status });
    if (assetType)     filters.push({ field: "assetType", value: assetType });
    if (ownershipType) filters.push({ field: "ownershipType", value: ownershipType });
    if (assignedTo)    filters.push({ field: "assignedTo", value: assignedTo });

    try {
      const rows = (await repo.fetchData(this.collectionName, filters)) || [];
      return rows.filter((r) => r?.metadata?.deletedFlag !== true);
    } catch (err) {
      console.error("[AssetService] Error fetching assets:", err);
      throw err;
    }
  }

  async fetchAssetById(id) {
    if (!id) throw new Error("Asset ID is required.");

    try {
      const assetDoc = await repo.fetchById(this.collectionName, id);
      if (!assetDoc) {
        console.warn(`[AssetService] No active asset document matches ID: ${id}`);
        return null;
      }
      if (assetDoc?.metadata?.deletedFlag === true) {
        console.warn(`[AssetService] Asset ${id} is soft-deleted.`);
        return null;
      }
      return assetDoc;
    } catch (error) {
      console.error(`[AssetService] Failed to fetch asset ${id}:`, error);
      throw error;
    }
  }

  async upsertData(targetAssetId, data, { isCreation = false } = {}) {
    if (!targetAssetId) throw new Error("Target Asset ID is required.");

  let baseData = data;
if (!isCreation) {
  try {
    const existing = await repo.fetchById(this.collectionName, targetAssetId);
    if (existing && typeof existing === "object") {
      const cleanedExisting = {};
      for (const k of AssetModel.ALLOWED_KEYS) {
        if (Object.prototype.hasOwnProperty.call(existing, k)) {
          cleanedExisting[k] = existing[k];
        }
      }
      baseData = { ...cleanedExisting, ...data };
    }
  } catch (err) {
    console.warn(
      `[AssetService] Could not preload asset ${targetAssetId} for merge:`,
      err?.message || err,
    );
  }
}

    // Normalize serialization string to safely check type ignoring case
    const normSerialization = String(baseData.serializationType || "")
      .toLowerCase()
      .trim();
    const isNonSerialized =
      normSerialization === "non-serialized" || normSerialization === "bulk";

    const parsedQty = Number(baseData.quantity);
    const validQty = !isNaN(parsedQty) && parsedQty >= 0 ? parsedQty : 1;

       const normalizedData = stripUndefined({
      ...baseData,
      id: targetAssetId,
      assetId: baseData.assetId || targetAssetId,

      // FIX: Map category/location so charts don't show "Unknown"
      assetType:     baseData.assetType     || baseData.category  || "Uncategorized",
      ownershipType: baseData.ownershipType || "Owned",
      locationCode:  baseData.locationCode  || baseData.location  || "Unknown Location",

      quantity: isNonSerialized
        ? validQty
        : (parsedQty > 0 ? parsedQty : 1),

      unitOfMeasurement: baseData.unitOfMeasurement || null,
    });

    if (isCreation && !normalizedData.companyId) {
      normalizedData.companyId = this.currentUser?.companyId || "";
    }

    const assetInstance = new AssetModel(normalizedData, { isCreation });

    if (!assetInstance.isValid({ isCreation })) {
      const validationErrors = assetInstance.getValidationErrors();
      throw new Error(`Asset validation failed: ${validationErrors.join(" ")}`);
    }

    const payload = stripUndefined(assetInstance.toPayload());
    const nowIso = new Date().toISOString();
    const updaterUser = this.currentUser?.email || "system";

    payload.metadata = {
      ...(payload.metadata || {}),
      modifiedAt: nowIso,
      modifiedBy: updaterUser,
    };

    if (isCreation) {
      payload.metadata.createdAt = nowIso;
      payload.metadata.createdBy = updaterUser;
    }

    return await repo.upsertData(this.collectionName, targetAssetId, payload);
  }

  async handleApprovalAction(assetId, level, approverEmail, action = "approve", comments = "") {
    if (!assetId || !level || !approverEmail) {
      throw new Error("Asset ID, level, and approver email are required.");
    }

    const asset = await this.fetchAssetById(assetId);
    if (!asset) throw new Error("Asset not found.");

    const approverList = Array.isArray(asset.approverList)
      ? [...asset.approverList]
      : [];

    const rowIndex = approverList.findIndex((r) => Number(r.level) === Number(level));
    if (rowIndex === -1) throw new Error(`Level ${level} not found in approver list.`);

    const row = { ...approverList[rowIndex] };
    const email = String(approverEmail).trim().toLowerCase();

    const users = Array.isArray(row.users) && row.users.length > 0
      ? row.users.map((e) => String(e).trim().toLowerCase())
      : (row.email ? [String(row.email).trim().toLowerCase()] : []);

    if (!users.includes(email)) {
      throw new Error(`User ${email} is not an assigned approver for Level ${level}.`);
    }

    const approvedUsers = Array.isArray(row.approvedUsers) ? [...row.approvedUsers] : [];
    const nowIso = new Date().toISOString();

    if (action === "reject") {
      row.status = `Rejected by ${email}`;
      row.actedBy = email;
      row.actedAt = nowIso;
      approverList[rowIndex] = row;

      const updated = { ...asset, approverList, approvalStatus: "Returned" };
      await this.upsertData(assetId, updated);
      return updated;
    }

    // ── Approve action ──
    if (!approvedUsers.includes(email)) {
      approvedUsers.push(email);
    }
    row.approvedUsers = approvedUsers;

     const allApproved = users.length > 0 && users.every((u) => approvedUsers.includes(u));
    row.actedBy = email;
    row.actedAt = nowIso;

    approverList[rowIndex] = row;

    // ── Derive aggregate status ──
    const sortedForStatus = [...approverList].sort(
      (a, b) => Number(a.level) - Number(b.level),
    );

    const anyRejected = sortedForStatus.some((r) => {
      const s = String(r.status || "").toLowerCase();
      return s.includes("reject") || s.includes("return");
    });

    const allLevelsApproved =
      sortedForStatus.length > 0 &&
      sortedForStatus.every((r) =>
        String(r.status || "").toLowerCase().startsWith("approved"),
      );

    let derivedStatus;
    if (anyRejected) {
      derivedStatus = "Returned";
    } else if (allLevelsApproved) {
      derivedStatus = "Approved";
    } else {
      const firstPending = sortedForStatus.find(
        (r) => !String(r.status || "").toLowerCase().startsWith("approved"),
      );
      derivedStatus = firstPending?.status || "Pending";
    }

    // ── Rebuild flattened approverEmails for the My Approvals query ──
    const approverEmails = Array.from(
      new Set(
        approverList.flatMap((r) => {
          const list =
            Array.isArray(r.users) && r.users.length > 0
              ? r.users
              : (r.email ? [r.email] : []);
          return list
            .map((e) => String(e || "").trim().toLowerCase())
            .filter((e) => e.includes("@"));
        }),
      ),
    );

    const updatedData = {
      ...asset,
      approverList,
      approvalStatus: derivedStatus,
      approverEmails,
    };

    await this.upsertData(assetId, updatedData);
    return updatedData;
  }

  async disposeAsset(
    assetId,
    { disposalDate, disposalReason, disposalRemarks = "", disposeQuantity = 1 } = {},
    operatorEmail = null,
  ) {
    const asset = await this.fetchAssetById(assetId);
    if (!asset) throw new Error("Asset not found.");

    const blockedTypes = ["consumable", "consumables", "spare part", "spare parts", "sparepart", "spareparts"];
    if (asset.assetType && blockedTypes.includes(String(asset.assetType).toLowerCase().trim())) {
      throw new Error(`Disposal is blocked for ${asset.assetType} assets (FSD §9). Use Consumptions instead.`);
    }

    if (!disposalDate) throw new Error("Disposal date is required.");
    if (!disposalReason) throw new Error("Disposal reason is required.");

    const nowIso = new Date().toISOString();
    let effectiveDisposalDate = nowIso;
    try {
      const parsed = new Date(disposalDate);
      if (!isNaN(parsed.getTime())) effectiveDisposalDate = parsed.toISOString();
    } catch (_) {
      effectiveDisposalDate = nowIso;
    }

    const isSerialized =
      asset.serializationType?.toLowerCase() === "serialized" ||
      asset.isSerialized === true ||
      asset.isSerialized === "true" ||
      asset.isSerialized === "yes" ||
      asset.quantity === null ||
      asset.quantity === undefined ||
      Number(asset.quantity) <= 1;

    const currentQty = Number(asset.quantity || 1);
    const qtyToDispose = isSerialized ? 1 : Number(disposeQuantity || 1);

    if (!isSerialized && (qtyToDispose <= 0 || qtyToDispose > currentQty)) {
      throw new Error(`Invalid disposal quantity. Must be between 1 and ${currentQty}.`);
    }

    const remainingQty = Math.max(0, currentQty - qtyToDispose);
    const isFullyDisposed = isSerialized || remainingQty === 0;
    const userEmail = operatorEmail || this.currentUser?.email || "system";

    const disposalRecord = {
      disposalDate: effectiveDisposalDate,
      disposalReason: String(disposalReason).trim(),
      disposalRemarks: String(disposalRemarks || "").trim(),
      disposedQuantity: qtyToDispose,
      remainingQuantity: remainingQty,
      disposedBy: userEmail,
      disposedAt: nowIso,
    };

    const disposalHistory = Array.isArray(asset.disposalHistory) ? asset.disposalHistory : [];
    const quantityHistory = Array.isArray(asset.quantityHistory) ? asset.quantityHistory : [];

    const updatedData = {
      ...asset,
      assetType: asset.assetType || "Unknown",
      ownershipType: asset.ownershipType || "Owned",
      status: isFullyDisposed ? "Disposed" : asset.status,
      quantity: isSerialized ? asset.quantity : remainingQty,
      disposalDate: effectiveDisposalDate,
      disposalReason: String(disposalReason).trim(),
      disposalRemarks: String(disposalRemarks || "").trim(),
      disposalHistory: [...disposalHistory, disposalRecord],
    };

    if (isFullyDisposed) {
      updatedData.assignedTo = null;
      updatedData.assignedToName = null;
      updatedData.assignedDepartmentId = null;
      updatedData.assignedOn = null;
    }

    if (!isSerialized) {
      updatedData.quantityHistory = [
        ...quantityHistory,
        {
          action: "Disposal",
          quantityChanged: -qtyToDispose,
          previousQuantity: currentQty,
          newQuantity: remainingQty,
          reason: disposalReason,
          date: effectiveDisposalDate,
          performedBy: userEmail,
        },
      ];
    }

    return await this.upsertData(assetId, updatedData);
  }

  async fetchChildAssets(primaryAssetId) {
    if (!primaryAssetId) return [];

    try {
      const rows = (await repo.fetchData(this.collectionName, [
        { field: "parentAssetId", value: primaryAssetId },
      ])) || [];

      return rows.filter((r) => r?.metadata?.deletedFlag !== true);
    } catch (error) {
      console.error(
        `[AssetService] Failed to fetch child assets for ${primaryAssetId}:`,
        error,
      );
      return [];
    }
  }

  async adjustQuantity(
    assetId,
    {
      adjustmentType = "increase",
      quantity = 1,
      reason = "",
      purchaseBillNumber = null,
      purchaseDate = null,
      billFileUrl = null,
      billFileName = null,
    } = {},
    operatorEmail = null,
  ) {
    const asset = await this.fetchAssetById(assetId);
    if (!asset) throw new Error("Asset not found.");

    const currentQty = Number(asset.quantity || 0);
    const qtyChange = Number(quantity || 0);

    if (qtyChange <= 0) {
      throw new Error("Quantity must be greater than 0.");
    }

    if (adjustmentType === "decrease" && qtyChange > currentQty) {
      throw new Error(`Cannot decrease quantity below current stock (${currentQty}).`);
    }

    const newQty =
      adjustmentType === "increase"
        ? currentQty + qtyChange
        : Math.max(0, currentQty - qtyChange);

    const nowIso = new Date().toISOString();
    const userEmail = operatorEmail || this.currentUser?.email || "system";

    const historyRecord = {
      action: adjustmentType === "increase" ? "Stock In" : "Stock Out",
      quantityChanged: adjustmentType === "increase" ? qtyChange : -qtyChange,
      previousQuantity: currentQty,
      newQuantity: newQty,
      reason: reason.trim(),
      purchaseBillNumber: purchaseBillNumber || null,
      purchaseDate: purchaseDate || null,
      billFileUrl: billFileUrl || null,
      billFileName: billFileName || null,
      date: nowIso,
      performedBy: userEmail,
    };

    const adjustmentRecord = {
      adjustedAt: nowIso,
      adjustedBy: userEmail,
      previousQuantity: currentQty,
      newQuantity: newQty,
      reason: reason.trim(),
      type: adjustmentType,
      quantity,
      purchaseBillNumber: purchaseBillNumber || null,
      purchaseDate: purchaseDate || null,
      billFileUrl: billFileUrl || null,
      billFileName: billFileName || null,
    };

    const existingHistory = Array.isArray(asset.quantityHistory)
      ? asset.quantityHistory
      : [];

    const existingAdjustments = Array.isArray(asset.quantityAdjustments)
      ? asset.quantityAdjustments
      : [];

    const updatedData = {
      ...asset,
      quantity: newQty,
      status: newQty === 0 ? "Unavailable" : (asset.status === "Unavailable" ? "Available" : asset.status),
      quantityHistory: [...existingHistory, historyRecord],
      quantityAdjustments: [...existingAdjustments, adjustmentRecord],
    };

    return await this.upsertData(assetId, updatedData);
  }
  /**
   * Hard-delete an asset record. Used for import rollback.
   * Falls back to soft delete if repo doesn't support deleteData.
   */
  async hardDeleteAsset(id) {
    if (!id) throw new Error("Asset ID is required.");
    if (typeof repo.deleteData !== "function") {
      return this.deleteAsset(id);
    }
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[AssetService] Hard-delete failed for ${id}:`, error);
      throw error;
    }
  }

  async deleteAsset(id) {
    if (!id) throw new Error("Asset ID is required.");

    try {
      const asset = await repo.fetchById(this.collectionName, id);
      if (!asset) throw new Error("Asset not found.");

      const nowIso = new Date().toISOString();
      const user = this.currentUser?.email || "system";

      await this.upsertData(id, {
        ...asset,
        assetType: asset.assetType || "Unknown",
        ownershipType: asset.ownershipType || "Owned",
        metadata: {
          ...(asset.metadata || {}),
          deletedFlag: true,
          modifiedAt: nowIso,
          modifiedBy: user,
        },
      });

      return { success: true };
    } catch (error) {
      console.error(`[AssetService] Failed to delete asset ${id}:`, error);
      throw error;
    }
  }

  /**
   * Marks an asset as physically tagged.
   * Admin can bypass photo requirement if { requirePhoto: false }
   */
  async markAssetTagged(
    assetId,
    { taggedBy, tagPhotoUrl = null, notes = "" } = {},
    currentUser = null,
    { requirePhoto = false } = {},
  ) {
    if (!assetId) {
      throw new Error("Asset ID is required to mark it as tagged.");
    }
    if (requirePhoto && !tagPhotoUrl) {
      throw new Error("A verification photo is required to tag an asset.");
    }

    const user = currentUser || this.currentUser;
    const actor =
      taggedBy ||
      user?.email ||
      user?.uid ||
      user?.id ||
      "system";

    const nowIso = new Date().toISOString();

    const asset = await this.fetchAssetById(assetId);
    if (!asset) throw new Error("Asset not found.");

    const previousHistory = Array.isArray(asset.quantityHistory)
      ? asset.quantityHistory
      : [];

    const updatedData = {
      ...asset,
      assetType: asset.assetType || "Unknown",
      ownershipType: asset.ownershipType || "Owned",

      isTagged: true,
      taggedAt: nowIso,
      taggedBy: actor,
      tagPhotoUrl: tagPhotoUrl || null,

      quantityHistory: [
        ...previousHistory,
        {
          action: "Tag Verified",
          quantityChanged: 0,
          previousQuantity: Number(asset.quantity || 1),
          newQuantity: Number(asset.quantity || 1),
          reason: notes || (tagPhotoUrl ? "Asset tag verified with photo" : "Asset tag verified (no photo)"),
          date: nowIso,
          performedBy: actor,
          photoUrl: tagPhotoUrl || null,
        },
      ],
    };

    const result = await this.upsertData(assetId, updatedData);

    try {
      const { logAuditEvent } = await import("../utils/logAudit");
      await logAuditEvent({
        actionType: "tag_verified",
        entityType: "Asset",
        entityId: assetId,
        description: `Asset tagged by ${actor}${notes ? ` — ${notes}` : ""}${tagPhotoUrl ? "" : " (no photo)"}`,
        companyId: asset.companyId,
        userId: user?.uid || user?.id,
        tagVerificationPhoto: tagPhotoUrl || null,
      });
    } catch (auditErr) {
      console.warn("[AssetService] Audit log failed for tagging:", auditErr);
    }

    return result;
  }

  /**
   * Clears tag verification on an asset (isTagged = false).
   */
  async markAssetUntagged(assetId, currentUser = null, notes = "") {
    if (!assetId) {
      throw new Error("Asset ID is required to untag an asset.");
    }

    const user = currentUser || this.currentUser;
    const actor =
      user?.email ||
      user?.uid ||
      user?.id ||
      "system";

    const nowIso = new Date().toISOString();

    const asset = await this.fetchAssetById(assetId);
    if (!asset) throw new Error("Asset not found.");

    const previousHistory = Array.isArray(asset.quantityHistory)
      ? asset.quantityHistory
      : [];

    const updatedData = {
      ...asset,
      assetType: asset.assetType || "Unknown",
      ownershipType: asset.ownershipType || "Owned",

      isTagged: false,
      taggedAt: null,
      taggedBy: null,
      tagPhotoUrl: null,

      quantityHistory: [
        ...previousHistory,
        {
          action: "Tag Cleared",
          quantityChanged: 0,
          previousQuantity: Number(asset.quantity || 1),
          newQuantity: Number(asset.quantity || 1),
          reason: notes || "Asset tag verification cleared",
          date: nowIso,
          performedBy: actor,
          photoUrl: null,
        },
      ],
    };

    const result = await this.upsertData(assetId, updatedData);

    try {
      const { logAuditEvent } = await import("../utils/logAudit");
      await logAuditEvent({
        actionType: "tag_cleared",
        entityType: "Asset",
        entityId: assetId,
        description: `Asset untagged by ${actor}${notes ? ` — ${notes}` : ""}`,
        companyId: asset.companyId,
        userId: user?.uid || user?.id,
      });
    } catch (auditErr) {
      console.warn("[AssetService] Audit log failed for untagging:", auditErr);
    }

    return result;
  }
}

export const assetService = new AssetService();